/**
 * SalesHeader Component
 * 
 * Displays the header information for the Sales component including:
 * - Mesa/Group/Pedido identification
 * - People counter (for mesas only)
 * - New pedido button (for delivery/pickup mode)
 */

import React from 'react';
import { Button } from 'react-bootstrap';
import { MdOutlineTableRestaurant, MdDeliveryDining } from 'react-icons/md';
import { FaStore, FaPlus, FaUser, FaMinus, FaComment } from 'react-icons/fa6';
import { VIEW_MODES, type ViewMode } from '../../utils';
import './SalesHeader.scss';

interface SalesHeaderProps {
  // Selection props
  selectedMesa?: any;
  selectedGroup?: any;
  selectedPedido?: any;
  salonName?: string;
  
  // Mode
  isPedidosMode: boolean;
  
  // People counter
  currentPeople: number;
  updatingPeople: boolean;
  onUpdatePeople: (newCount: number) => void;
  
  // Actions
  onClearPedidoSelection?: () => void;
  setViewMode: (mode: ViewMode) => void;
  
  // Comment
  pedidoComment?: string;
  onOpenCommentModal?: () => void;
  
  // Comments viewer
  pedidos?: any[];
  onOpenCommentsViewer?: () => void;
  
  // View mode to determine button behavior
  viewMode?: string;
}

const SalesHeader: React.FC<SalesHeaderProps> = ({
  selectedMesa,
  selectedGroup,
  selectedPedido,
  salonName,
  isPedidosMode,
  currentPeople,
  updatingPeople,
  onUpdatePeople,
  onClearPedidoSelection,
  setViewMode,
  pedidoComment,
  onOpenCommentModal,
  pedidos = [],
  onOpenCommentsViewer,
  viewMode,
}) => {
  // Count pedidos with comments
  const commentsCount = pedidos.filter(p => p.comment && p.comment.trim() !== '').length;
  const isOrderMode = viewMode === 'order';

  return (
    <div className="sales-header">
      <div className="sales-title-row">
        {isPedidosMode && selectedPedido ? (
          <>
            {selectedPedido.pedido_type_id === 3 ? (
              <MdDeliveryDining size={22} className="table-icon" />
            ) : (
              <FaStore size={22} className="table-icon" />
            )}
            <div className="table-badge">
              #{selectedPedido.pedido_id}
            </div>
            <div className="salon-name">
              {selectedPedido.pedido_type_id === 3 ? 'Delivery' : 'Retiro en Local'}
            </div>
            {/* Add New Pedido Button */}
            <Button
              variant="none"
              className="new-pedido-btn"
              onClick={() => {
                // Clear the pedido selection and switch to create mode
                if (onClearPedidoSelection) {
                  onClearPedidoSelection();
                }
                setViewMode(VIEW_MODES.CREATE_PEDIDO);
              }}
              title="Crear nuevo pedido"
            >
              <FaPlus size={14} />
            </Button>
          </>
        ) : isPedidosMode && !selectedPedido ? (
          // Show generic delivery/pickup header when in pedidos mode but no specific pedido selected
          <>
            <MdDeliveryDining size={22} className="table-icon" />
            <div className="salon-name">Delivery / Retiro en Local</div>
          </>
        ) : selectedGroup ? (
          <>
            <MdOutlineTableRestaurant size={22} className="table-icon" />
            <div className="table-badge group-badge" key={selectedGroup.pedido_group_id}>
              {selectedGroup.name}
            </div>
            <div className="salon-name">Grupo de Pedidos</div>
          </>
        ) : selectedMesa ? (
          <>
            <MdOutlineTableRestaurant size={22} className="table-icon" />
            <div className="table-badge" key={selectedMesa.id}>
              {(selectedMesa.tag && selectedMesa.tag.trim() !== '') ? selectedMesa.tag : selectedMesa.num}
            </div>
            {salonName && (
              <div className="salon-name">{salonName}</div>
            )}
          </>
        ) : null}
        
        {!isPedidosMode && selectedMesa && !selectedGroup && (
          <div className="people-controls">
            <div className="people-label">
              <FaUser size={11} /> 
            </div>
            <button
              className="people-btn btn"
              onClick={() => onUpdatePeople(currentPeople - 1)}
              disabled={currentPeople <= 1 || updatingPeople}
            >
              <FaMinus size={10} />
            </button>
            <div className="people-count">{currentPeople}</div>
            <button
              className="people-btn btn"
              onClick={() => onUpdatePeople(currentPeople + 1)}
              disabled={updatingPeople}
            >
              <FaPlus size={10} />
            </button>
          </div>
        )}
        
        {/* Smart Comment button - behavior changes based on view mode */}
        {onOpenCommentModal && onOpenCommentsViewer && (
          <Button
            variant="none"
            className={`comment-icon-button ${pedidoComment && isOrderMode ? 'has-comment' : ''} ${!isOrderMode && commentsCount > 0 ? 'has-badge' : ''}`}
            onClick={() => isOrderMode ? onOpenCommentModal() : onOpenCommentsViewer()}
            title={isOrderMode ? (pedidoComment || 'Agregar comentario') : `Ver ${commentsCount} comentario${commentsCount !== 1 ? 's' : ''}`}
          >
            <FaComment size={16} />
            {pedidoComment && isOrderMode && <span className="comment-indicator"></span>}
            {!isOrderMode && commentsCount > 0 && <span className="comment-badge">{commentsCount}</span>}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SalesHeader;
